import React, { useEffect, useState, useRef  } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Modal from '../Modal/Modal';
import "./DetailSearch.css";
import { fetchMenuData } from '../../actions/sidemenu';
import { fetchUserData } from "../../actions/user";

import DetailSearchCommon from "./DetailSearchCommon";
import KeisouForm from "./KeisouForm";
import KeisokuForm from "./KeisokuForm";
import SouchiForm from "./SouchiForm";
import Breadcrumb from "../Breadcrumb";
import useResizableTable from "../../hooks/useResizableTable";


/**
 * 詳細検索画面メインコンポーネント
 */
const DetailSearch = React.memo(({ user, menuData = [], isFetching, isFetchingCount, error, headers, data, count, keys, fetchDetailSearch, fetchDetailSearchCount, fetchDataType, document_type, document_category }) => {

  const { deptId, folderId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageGroup, setPageGroup] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const scrollRef = useRef(null);

  // 検索条件（詳細フォームから更新される）
  const [criteria, setCriteria] = useState({
    data_limit: 50,
    searchMode: "all",
  });
  const isUnlimited = criteria.data_limit === "unlimited";

  const dispatch = useDispatch();
  const location = useLocation();

  // 部署名・フォルダ名の取得
  const currentDept = menuData.find((d) => d.department_id === deptId);
  const departmentName = currentDept?.department_name || "";
  const currentFolder = currentDept?.folder?.find((f) => f.folder_id === folderId);
  const folderName = currentFolder?.folder_name || "";
  
  /**
   * --- テーブル列リサイズ機能 ---
   * headers・folderId が変わると再実行
   */
  useResizableTable([headers, folderId]);

  /**
   * マウント時：検索結果をクリア
   */
  useEffect(() => {
    dispatch({ type: "CLEAR_DETAIL_SEARCH" });
  }, [dispatch]);

  /**
   * メニュー情報の取得（token がある場合のみ）
   */
  const token = localStorage.getItem('accessToken');

  useEffect(() => {
    if (!token) return;  

    const fetchData = async () => {
      try {
        await dispatch(fetchMenuData());
      } catch (error) {
        console.error('Error fetching menu data:', error);
      }
    };

    fetchData();
  }, [token, dispatch]);


  /**
   * ユーザー情報取得
   */
  useEffect(() => {
    if (token) {
      dispatch(fetchUserData());
    }
  }, [dispatch, token]);


  /**
   * フォルダに応じてドキュメント種別を取得
   */
  useEffect(() => {
    if (!deptId) return;

    fetchDataType(deptId, folderId ?? null);

  }, [deptId, folderId, fetchDataType]);


  /**
   * 画面遷移時に検索条件・ページ番号を初期化
   */
  useEffect(() => {
    setCriteria({
      data_limit: 50,
      searchMode: "all",
    });
    setCurrentPage(1);
    setPageGroup(0);
  }, [location.key]);


  /**
   * 詳細フォームからの値変更を受け取り、criteria を更新
   */
  const handleFormChange = (name, value) => {
    setCriteria(prev => ({ ...prev, [name]: value }));
  };


  /**
   * 検索ボタン押下時の処理
   */
  const handleSearch = async () => {
    dispatch({ type: "CLEAR_DETAIL_SEARCH" });

    const dateCond = criteria.dateCondition;

    // ▼ 日付の整合性チェック
    if (dateCond && !dateCond.isAll && dateCond.mode === "date") {
      const { dateFrom: from, dateTo: to } = dateCond;

      if (from && to && new Date(from) > new Date(to)) {
        setModalMessage("正しい期間を入力してください。");
        setIsModalOpen(true);
        return;
      }
    }

    setCurrentPage(1);
    setPageGroup(0);

    try {
      // ① データ取得（必ず）
      const dataPromise = fetchDetailSearch(criteria, deptId, folderId);

      // ② 件数取得（unlimited のみ）
      const countPromise = isUnlimited
        ? fetchDetailSearchCount(criteria, deptId, folderId)
        : null;

      // ③ data 完了を待つ
      await dataPromise;

      // ④ data 後スクロール
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }

      // ⑤ count 完了後スクロール（unlimited のみ）
      if (countPromise) {
        countPromise
          .then(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: "smooth",
              });
            }
          })
          .catch(err => {
            console.error("件数取得エラー:", err);
          });
      }
    } catch (err) {
      console.error("検索エラー:", err);
    }
  };

  /**
   * 部署別にフォームコンポーネントを出し分け
   */
  const renderFormByDept = () => {
    const commonProps = {
      folderName,
      onFormChange: handleFormChange,
      criteria,
      document_category,
      document_type,
    };

    switch (deptId) {
      case "D001":
        return <KeisouForm {...commonProps} />;
      case "D002":
        return <SouchiForm {...commonProps} />;
      case "D003":
        return <KeisokuForm {...commonProps} />;
      default:
        return <div>対応する詳細検索フォームがありません。</div>;
    }
  };

  /**
   * --- ページング計算 ---
   */
  const itemsPerPage = 100;
  const currentData = isUnlimited
    ? data || []
    : (data || []).slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );
  const totalPages = isUnlimited
    ? Math.ceil((count || 0) / itemsPerPage)
    : Math.ceil((data?.length || 0) / itemsPerPage);
  const displayTotalCount = isUnlimited
    ? count || 0
    : data?.length || 0;

  const handlePageChange = (page) => {
    setCurrentPage(page);

    if (!isUnlimited) {
      // client-side paging
      return;
    }

    // server-side paging
    const key = keys?.[page - 1] ?? null;
    fetchDetailSearch(criteria, deptId, folderId, key);
  };

  const handleDocumentView = (row) => {
    if (!row?.doc_id) return;
    window.open(`/viewId/${row.doc_id}/view`);
  };

  return (
    <div id="DetailMain">
      <div id="DetailContents">

        {/* モーダル */}
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            message={modalMessage}
          />
        )}

        {/* パンくずリスト */}
        <Breadcrumb
          userName={user?.user_name || ""}
          departmentName={departmentName}
          folderName={folderName}
          deptId={deptId}
          folderId={folderId}
        />

        {/* --- 詳細検索メイン枠 --- */}
        <div className="detail-search-wrap" ref={scrollRef}>
          <div className="detail-search__container" style={{ maxHeight: '90vh' }}>
              
            <DetailSearchCommon
              folderName={folderName}
              searchLimit={criteria.data_limit}
              onChange={(key, val) => handleFormChange(key, val)}
              headers={headers}
              currentData={currentData}
              currentPage={currentPage}
              pageGroup={pageGroup}
              totalPages={totalPages}
              totalCount={displayTotalCount}
              setCurrentPage={setCurrentPage}
              setPageGroup={setPageGroup}
              onSearch={handleSearch}
              onPageChange={handlePageChange}
              handleDocumentView={handleDocumentView}
              isFetchingCount={isFetchingCount}
            >
              {renderFormByDept()}
            </DetailSearchCommon>

          </div>
        </div>
      </div>

      {/* --- 読み込みスピナー（検索中のオーバーレイ） --- */}
      <div className={`overlay ${isFetching ? 'active' : ''}`}>
        <div className={`spinner ${isFetching ? 'active' : ''}`}></div>
      </div>

    </div>
  );
});

DetailSearch.displayName = "DetailSearch";

DetailSearch.propTypes = {
  user: PropTypes.object,
  menuData: PropTypes.array,
  isFetching: PropTypes.bool,
  isFetchingCount: PropTypes.bool,
  error: PropTypes.any,
  headers: PropTypes.array,
  data: PropTypes.array,
  count: PropTypes.number.isRequired,
  keys: PropTypes.array.isRequired,
  fetchDetailSearch: PropTypes.func.isRequired,
  fetchDetailSearchCount: PropTypes.func.isRequired,
  fetchDataType: PropTypes.func.isRequired,
  document_type: PropTypes.array,
  document_category: PropTypes.array,
};

export default DetailSearch;
